// ─── Public Status Page — one incident, in the list ──────────────────
//
// A whole-card <a> to /incident/<refId>, so the browser's own affordances (open
// in a new tab, copy link, middle click) all work and only a plain left click is
// intercepted. The excerpt is the LATEST update's text when there is one, not
// the original body: during a live incident the newest sentence is the one the
// reader needs, and the opening "we are investigating" is the least useful line
// on the page an hour later.

import type { MouseEvent } from 'react';
import type { PublicStatusIncident, PublicStatusUpdate } from '@pcp/shared';
import { da } from '../i18n/da';
import { fmtDateTime } from '../lib/format';
import { levelForStatus, NO_DATA_LEVEL, STATUS_LEVELS, type StatusLevel } from '../lib/levels';
import { isPlainLeftClick } from '../router';
import { SIcon, type SIconName } from '../ui/SIcon';
import { stripHtml } from '../ui/RichText';

export interface IncidentCardProps {
  incident: PublicStatusIncident;
  href: string;
  onOpen: (incident: PublicStatusIncident) => void;
}

/**
 * The icon + colour that stand for this incident. Kind wins over impact: a
 * planned maintenance window is blue with a wrench even when its impact is a
 * full outage, because "we are doing this on purpose" is the first thing the
 * reader must take from the row.
 */
export function incidentTone(incident: PublicStatusIncident): { icon: SIconName; color: string } {
  if (incident.kind === 'maintenance') {
    return { icon: 'maintenance', color: STATUS_LEVELS.maintenance.color };
  }
  if (incident.kind === 'information') {
    return { icon: 'info', color: STATUS_LEVELS.maintenance.color };
  }
  const level = levelForStatus(incident.impact);
  // An incident that has not (yet) been tied to a component impact reads as
  // information rather than as a false all-clear.
  if (incident.impact === 'available') return { icon: 'info', color: NO_DATA_LEVEL.color };
  return { icon: level.icon, color: level.color };
}

/**
 * Which pill the lifecycle status is painted in. Closed is always the success
 * tint — the point of the history list is that the green ones are over — and an
 * open incident borrows its own impact colour, falling back to neutral when it
 * has no impact yet.
 */
export function incidentPillLevel(incident: PublicStatusIncident): StatusLevel {
  if (incident.state === 'closed') return STATUS_LEVELS.available;
  if (incident.impact === 'available') return NO_DATA_LEVEL;
  return levelForStatus(incident.impact);
}

/** Newest update by occurredAt — the payload's array order is not guaranteed. */
export function latestUpdate(updates: PublicStatusUpdate[] | undefined): PublicStatusUpdate | null {
  if (!updates || updates.length === 0) return null;
  return updates.reduce((newest, candidate) =>
    new Date(candidate.occurredAt).getTime() > new Date(newest.occurredAt).getTime() ? candidate : newest,
  );
}

export function IncidentCard({ incident, href, onOpen }: IncidentCardProps) {
  const tone = incidentTone(incident);
  const pill = incidentPillLevel(incident);
  const excerpt = stripHtml(latestUpdate(incident.updates)?.body ?? incident.body ?? '');
  const affected = (incident.components ?? []).map((component) => component.name).join(' · ');

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onOpen(incident);
  };

  return (
    <a className="sp-incident" href={href} onClick={handleClick}>
      <div className="sp-incident-head">
        <SIcon name={tone.icon} size={18} style={{ color: tone.color }} />
        <h3 className="sp-incident-title">{incident.title}</h3>
        <SIcon name="chevronRight" size={18} className="sp-incident-chevron" />
      </div>

      <div className="sp-incident-meta">
        <span className="sp-incident-ref">{incident.refId}</span>
        <span className={`sp-pill sp-pill--${pill.modifier}`}>{incident.statusLabel}</span>
        <span>{fmtDateTime(incident.occurredAt)}</span>
        {incident.pinned && <span className="sp-incident-pin">{da.incidentPinned}</span>}
        {(incident.labels ?? []).length > 0 && (
          <span className="sp-incident-labels">
            {incident.labels.map((label) => (
              <span key={label} className={`sp-label sp-label--${label}`}>
                {da.filterLabels[label]}
              </span>
            ))}
          </span>
        )}
      </div>

      {affected && <p className="sp-incident-components">{affected}</p>}
      {excerpt && <p className="sp-incident-excerpt">{excerpt}</p>}
    </a>
  );
}
